import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AcceptInvite() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token");
  const [state, setState] = useState("loading"); // loading | ok | invalid | done
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/team/invite/validate/${token}`).then((r) => { setInfo(r.data); setState("ok"); }).catch(() => setState("invalid"));
  }, [token]);

  const submit = async () => {
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try { await api.post("/team/accept", { token, password: pw }); setState("done"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not activate account"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
      <Card className="border shadow-none p-8 w-full max-w-md" data-testid="accept-invite-card">
        {state === "loading" && <div className="text-center py-6"><Loader2 className="w-6 h-6 animate-spin text-indigo-500 mx-auto" /></div>}
        {state === "invalid" && (
          <div className="text-center space-y-3">
            <ShieldAlert className="w-10 h-10 text-red-500 mx-auto" />
            <h2 className="font-display font-bold text-lg">Invalid or expired invitation</h2>
            <p className="text-sm text-slate-500">Ask your account admin to resend the invite.</p>
            <Button variant="outline" onClick={() => nav("/login")}>Go to login</Button>
          </div>
        )}
        {state === "ok" && (
          <div className="space-y-4">
            <div><h2 className="font-display font-bold text-xl">Welcome{info?.name ? `, ${info.name}` : ""}!</h2>
              <p className="text-sm text-slate-500">You're joining as <b>{info?.role}</b> ({info?.email}). Set a password to activate your account.</p></div>
            <div><Label>Create password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" data-testid="accept-password" /></div>
            <Button className="w-full" onClick={submit} disabled={busy} data-testid="accept-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Activate & Continue</Button>
          </div>
        )}
        {state === "done" && (
          <div className="text-center space-y-3">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <h2 className="font-display font-bold text-lg">Account activated!</h2>
            <p className="text-sm text-slate-500">You can now sign in with your email and new password.</p>
            <Button onClick={() => nav("/login")} data-testid="accept-goto-login">Go to login</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
